import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Transactions.css';
import { API_BASE_URL } from '../../constrants/constrant';

const Transactions = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchTransactions();
    }, []);

    const fetchTransactions = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            if (!token) {
                setError('Please login to view your transactions');
                setLoading(false);
                return;
            }

            const response = await axios.get(`${API_BASE_URL}/profile/purchases`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            setTransactions(response.data.purchases || []);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching transactions:', err);
            setError(err.response?.data?.message || 'Failed to load transactions');
            setLoading(false);
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'completed':
                return 'status-badge status-completed';
            case 'pending':
                return 'status-badge status-pending';
            case 'failed':
                return 'status-badge status-failed';
            case 'refunded':
                return 'status-badge status-refunded';
            default:
                return 'status-badge';
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="transactions-container">
                <div className="transactions-loading">
                    <div className="spinner"></div>
                    <p>Loading your transactions...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="transactions-container">
                <div className="transactions-error">
                    <i className="error-icon">⚠️</i>
                    <h3>Oops!</h3>
                    <p>{error}</p>
                    <button onClick={fetchTransactions} className="retry-button">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="transactions-container">
            <div className="transactions-header">
                <h2>My Purchases</h2>
                <p className="transactions-subtitle">
                    View all your app purchase history and transactions
                </p>
            </div>

            {transactions.length === 0 ? (
                <div className="transactions-empty">
                    <div className="empty-icon">🛍️</div>
                    <h3>No Purchases Yet</h3>
                    <p>You haven't purchased any apps yet. Browse our store to find amazing apps!</p>
                </div>
            ) : (
                <div className="transactions-list">
                    {transactions.map((transaction) => (
                        <div key={transaction.id} className="transaction-card">
                            <div className="transaction-app-info">
                                <img
                                    src={transaction.app.icon_url}
                                    alt={transaction.app.name}
                                    className="transaction-app-icon"
                                    onError={(e) => {
                                        e.target.src = '/placeholder-app-icon.png';
                                    }}
                                />
                                <div className="transaction-app-details">
                                    <h3 className="transaction-app-name">{transaction.app.name}</h3>
                                    <p className="transaction-app-developer">
                                        by {transaction.app.developer || 'Unknown Developer'}
                                    </p>
                                    {transaction.app.category && (
                                        <span className="transaction-app-category">
                                            {transaction.app.category}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="transaction-details">
                                <div className="transaction-detail-item">
                                    <span className="detail-label">Transaction ID</span>
                                    <span className="detail-value">#{transaction.id}</span>
                                </div>
                                <div className="transaction-detail-item">
                                    <span className="detail-label">Amount</span>
                                    <span className="detail-value transaction-amount">
                                        ₹{parseFloat(transaction.amount).toFixed(2)}
                                    </span>
                                </div>
                                <div className="transaction-detail-item">
                                    <span className="detail-label">Payment Method</span>
                                    <span className="detail-value">
                                        {transaction.payment_method?.charAt(0).toUpperCase() +
                                            transaction.payment_method?.slice(1) || 'N/A'}
                                    </span>
                                </div>
                                <div className="transaction-detail-item">
                                    <span className="detail-label">Date</span>
                                    <span className="detail-value">
                                        {formatDate(transaction.purchased_at)}
                                    </span>
                                </div>
                                <div className="transaction-detail-item">
                                    <span className="detail-label">Status</span>
                                    <span className={getStatusBadgeClass(transaction.status)}>
                                        {transaction.status?.charAt(0).toUpperCase() +
                                            transaction.status?.slice(1)}
                                    </span>
                                </div>
                            </div>

                            {transaction.transaction_id && (
                                <div className="transaction-footer">
                                    <span className="transaction-id-label">
                                        Payment ID: {transaction.transaction_id}
                                    </span>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {transactions.length > 0 && (
                <div className="transactions-summary">
                    <div className="summary-item">
                        <span className="summary-label">Total Purchases</span>
                        <span className="summary-value">{transactions.length}</span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">Total Spent</span>
                        <span className="summary-value">
                            ₹{transactions.reduce((sum, t) => sum + parseFloat(t.amount), 0).toFixed(2)}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Transactions;
